import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Link, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';

interface GoogleSheetsInputProps {
    onUrlSubmit: (url: string) => void;
    isLoading?: boolean;
}

export function GoogleSheetsInput({ onUrlSubmit, isLoading = false }: GoogleSheetsInputProps) {
    const { language } = useLanguage();
    const isEs = (language as string) === 'es';
    const [url, setUrl] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isValid, setIsValid] = useState(false);

    const validateUrl = (inputUrl: string) => {
        // Regex mejorado para verificar que es una URL de Google Sheets
        // Acepta: docs.google.com/spreadsheets o spreadsheets.google.com
        const regex = /(docs\.google\.com\/spreadsheets|spreadsheets\.google\.com)/i;

        if (regex.test(inputUrl)) {
            setIsValid(true);
            setError(null);
        } else {
            setIsValid(false);
            // Solo mostramos error si hay texto, para no molestar mientras escribe al principio
            if (inputUrl.length > 0) {
                setError(isEs ? 'La URL no parece válida. Asegúrate de que es un Google Sheet.' : 'The URL does not appear valid. Make sure it is a Google Sheet.');
            }
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value;
        setUrl(input);
        validateUrl(input);
    };

    const handleSubmit = () => {
        if (isValid && url) {
            onUrlSubmit(url);
        }
    };

    return (
        <div className="w-full space-y-2">
            <label className="block text-sm font-medium text-gray-700">
                {isEs ? 'O conecta una Google Sheet' : 'Or connect a Google Sheet'}
            </label>

            <div className="flex gap-2">
                <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Link className="h-4 w-4 text-[var(--text-secondary)]" />
                    </div>
                    <input
                        type="text"
                        className={`
              block w-full pl-10 pr-3 py-2 border rounded-md leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-1 sm:text-sm transition-colors
              ${error
                                ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                                : isValid
                                    ? 'border-green-300 focus:border-green-500 focus:ring-green-500'
                                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                            }
              ${isLoading ? 'bg-gray-50 text-[var(--text-tertiary)] cursor-not-allowed' : ''}
            `}
                        placeholder="https://docs.google.com/spreadsheets/d/..."
                        value={url}
                        onChange={handleChange}
                        disabled={isLoading}
                    />
                    {/* Icono de estado a la derecha */}
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        {isValid && !isLoading && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                        {error && <AlertCircle className="h-4 w-4 text-red-500" />}
                    </div>
                </div>

                <button
                    onClick={handleSubmit}
                    disabled={!isValid || isLoading}
                    className={`
            inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white 
            ${!isValid || isLoading
                            ? 'bg-gray-300 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                        }
          `}
                >
                    {isLoading ? (isEs ? 'Conectando...' : 'Connecting...') : (isEs ? 'Conectar' : 'Connect')}
                    {!isLoading && <ArrowRight className="ml-2 h-4 w-4" />}
                </button>
            </div>

            {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
            {isValid && !error && (
                <p className="text-xs text-green-600 mt-1">
                    {isEs ? 'URL válida detectada. Listo para conectar.' : 'Valid URL detected. Ready to connect.'}
                </p>
            )}
        </div>
    );
}